import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {AuthService} from "../_services/auth.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {SnackbarService} from "../_services/snackbar.service";
import {ConfirmDialogComponent} from "../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../_types/shared";
import {User} from "../_types/user";
import {ImageCroppedEvent} from "ngx-image-cropper";
import {toDateOnly} from "../_utils/date-only";
import {UK_DATE_ADAPTER_PROVIDERS} from "../_utils/uk-date-adapter";

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrl: './signup.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    providers: UK_DATE_ADAPTER_PROVIDERS
})
export class SignupComponent implements OnInit {
    user: User | undefined;
    signupForm: FormGroup;
    imgURL: string | ArrayBuffer = 'assets/images/blankProfile.png';
    loading: boolean = false;
    photoFile: string | ArrayBuffer | null = null;
    readonly maxDate: Date = new Date();
    showCropper: boolean = false;
    imageChangedEvent: Event | null = null;
    isRaa: boolean = true;
    councilLogo: string | undefined;
    authorityName: string | undefined;

    constructor(private router: Router,
                private authService: AuthService,
                public dialog: MatDialog,
                private formBuilder: FormBuilder,
                private snackbarService: SnackbarService,) {
        this.signupForm = this.formBuilder.group({
            first_name: [''],
            last_name: [''],
            email: [''],
            username: [''],
            about: ['', Validators.required],
            dob: ['', Validators.required],
            emailNotifications: [''],
        });
        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
    }

    ngOnInit(): void {
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })
        this.authService.getDynamicSettings().then((r): void => {
            this.councilLogo = r.authority_logo;
            this.authorityName = r.authority_name;
            if (r.is_raa) {
                this.signupForm.get('about')?.clearValidators();
            }
        });
        if (this.user?.role?.role_type !== 'young_person' || this.user.dob) {
            this.signupForm.get('dob')?.clearValidators();
        }
        this.authService.refreshUser().then((r): void => {
            this.user = r;
            if (r.photo) {
                this.imgURL = r.photo;
            }
            this.signupForm.patchValue({
                first_name: r.first_name,
                last_name: r.last_name,
                email: r.email,
                username: r.username,
                emailNotifications: r.email_notifications,
                about: r.about_me,
                dob: r.dob
            })
            this.signupForm.markAllAsTouched();
        });
    }

    imageCropped(event: ImageCroppedEvent): void {
        if (!event.blob) return;
        let file: File = new File([event.blob], "profile_picture.png");
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(event.blob);
        const that: this = this;
        reader.onloadend = function (): void {
            const base64data: string | ArrayBuffer | null = reader.result;
            if (base64data) {
                that.imgURL = base64data;
            }
        };
    }

    removeImage(): void {
        this.imgURL = 'assets/images/blankProfile.png';
    }

    onFileChanged(event: Event): void {
        const element = event.currentTarget as HTMLInputElement;
        const fileList: FileList | null = element.files;
        if (!fileList || fileList.length <= 0) return;

        const file: File = fileList[0];

        var mimeType: string = file.type;
        if (mimeType.match(/image\/*/) == null) {
            this.snackbarService.openSnackBar("Only images are supported.", 'error')
            return;
        }

        var reader: FileReader = new FileReader();
        reader.readAsDataURL(file);
        this.showCropper = true;
        this.imageChangedEvent = event;
        reader.onload = (_event): void => {
            if (reader.result) {
                this.imgURL = reader.result;
            } else {
                this.snackbarService.openSnackBar("Reader result returned null.", 'error')
            }
        }
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
    }

    registerCheck(): void {
        if (this.isRaa) {
            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Profile picture and about me text can be seen by everyone on the platform, including birth family and adoptive family members',
                    title: 'Are you sure you want to continue?',
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp) {
                    this.register();
                }
            })
        } else {
            this.register();
        }
    }

    register(): void {
        this.loading = true;
        let user = {
            about_me: this.signupForm.value.about,
            email_notifications: this.signupForm.value.emailNotifications,
            photo: '',
            has_completed_onboarding: 'true',
            dob: toDateOnly(this.signupForm.value.dob)
        }
        if (this.photoFile) {
            user.photo = this.photoFile as string;
        }
        this.authService.patchUser(this.clean(user)).subscribe({
            next: r => {
                this.authService.setUser(r)
                this.snackbarService.openSnackBar("Thanks for registering! Don't forget to bookmark this page to access later.", 'success')

                if (this.user?.role.role_type == "young_person") {
                    // Will be child or adult or admin in prod
                    this.router.navigate(['/timeline']);
                } else if (this.user?.role.role_type == "admin") {
                    this.router.navigate(['/reporting']);
                } else {
                    this.router.navigate(['/people']);
                }
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    clean(obj: any) {
        for (var propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj
    }

    getBase64(file: Blob): Promise<string | ArrayBuffer | null> {
        return new Promise(resolve => {
            const reader: FileReader = new FileReader();
            reader.onloadend = (): void => {
                resolve(reader.result);
            };
            return reader.readAsDataURL(file);
        });
    }
}
